import type { BaseCellValue, FieldADTValue, TypeInstanceMap, VariableADTvalue } from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'
import type { JSONContent } from '@tiptap/core'
import {
    addDays,
    addHours,
    addMinutes,
    addMonths,
    addSeconds,
    addWeeks,
    addYears,
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
    differenceInMonths,
    differenceInSeconds,
    differenceInWeeks,
    differenceInYears,
    format,
    getDate,
    getDay,
    getHours,
    getMinutes,
    getMonth,
    getSeconds,
    getWeek,
    getYear,
    isSameDay,
    isSameHour,
    isSameMinute,
    isSameMonth,
    isSameSecond,
    isSameYear,
    isValid,
    parse
} from 'date-fns'
import jsep from 'jsep'
import { isEmpty } from 'rambda'

import { generateText, isEmptyRichTextValue } from '../../components/RichText/helper'

type FormulaValue =
    | { kind: 'null' }
    | { kind: 'number'; value: number }
    | { kind: 'date'; value: number }
    | { kind: 'bool'; value: boolean }
    | { kind: 'text'; value: string }
    | { kind: 'stringArray'; value: string[] }
    | { kind: 'numberArray'; value: number[] }
    | { kind: 'error' }

const NULL: FormulaValue = { kind: 'null' }
const ERROR: FormulaValue = { kind: 'error' }

function isError(v: FormulaValue): v is { kind: 'error' } {
    return v.kind === 'error'
}

function isNull(v: FormulaValue): v is { kind: 'null' } {
    return v.kind === 'null'
}

function asNumber(v: FormulaValue): number | undefined {
    if (v.kind === 'number' || v.kind === 'date') {
        return v.value
    }
    return undefined
}

function asBool(v: FormulaValue): boolean | undefined {
    return v.kind === 'bool' ? v.value : undefined
}

function asText(v: FormulaValue): string | undefined {
    return v.kind === 'text' ? v.value : undefined
}

function asArray(v: FormulaValue): string[] | undefined {
    return v.kind === 'stringArray' ? v.value : undefined
}

function asNumberArray(v: FormulaValue): number[] | undefined {
    return v.kind === 'numberArray' ? v.value : undefined
}

function asAnyArrayLen(v: FormulaValue): number | undefined {
    if (v.kind === 'stringArray') return v.value.length
    if (v.kind === 'numberArray') return v.value.length
    return undefined
}

function toStringValue(v: FormulaValue): string {
    switch (v.kind) {
        case 'null':
            return ''
        case 'number':
            return String(v.value)
        case 'date':
            return String(v.value)
        case 'bool':
            return v.value ? 'true' : 'false'
        case 'text':
            return v.value
        case 'stringArray':
            return v.value.join(',')
        case 'numberArray':
            return v.value.map(String).join(',')
        case 'error':
            return ''
    }
}

function normalizeName(name: string): string {
    return name.trim().toLowerCase()
}

function equalsValue(a: FormulaValue, b: FormulaValue): boolean {
    if (a.kind !== b.kind) {
        // Allow number/date comparisons by treating both as numbers when possible
        const an = asNumber(a)
        const bn = asNumber(b)
        if (an !== undefined && bn !== undefined) {
            return an === bn
        }
        return false
    }
    switch (a.kind) {
        case 'null':
            return true
        case 'number':
            return a.value === (b as any).value
        case 'date':
            return a.value === (b as any).value
        case 'bool':
            return a.value === (b as any).value
        case 'text':
            return a.value === (b as any).value
        case 'stringArray': {
            const bb = b as any as { value: string[] }
            if (a.value.length !== bb.value.length) {
                return false
            }
            for (let i = 0; i < a.value.length; i++) {
                if (a.value[i] !== bb.value[i]) {
                    return false
                }
            }
            return true
        }
        case 'numberArray': {
            const bb = b as any as { value: number[] }
            if (a.value.length !== bb.value.length) {
                return false
            }
            for (let i = 0; i < a.value.length; i++) {
                if (a.value[i] !== bb.value[i]) {
                    return false
                }
            }
            return true
        }
        case 'error':
            return true
    }
}

type Fn = (args: FormulaValue[], receiver?: FormulaValue) => FormulaValue

function requireArgs(args: FormulaValue[], min: number): boolean {
    return args.length >= min
}

function boolFromUnknown(v: FormulaValue): boolean | undefined {
    if (v.kind === 'bool') {
        return v.value
    }
    if (v.kind === 'number') {
        return v.value !== 0 && !Number.isNaN(v.value)
    }
    if (v.kind === 'text') {
        return v.value.trim().length > 0
    }
    if (v.kind === 'stringArray') {
        return v.value.length > 0
    }
    if (v.kind === 'numberArray') {
        return v.value.length > 0
    }
    if (v.kind === 'null') {
        return false
    }
    return undefined
}

function numberFromUnknown(v: FormulaValue): number | undefined {
    if (v.kind === 'number') {
        return v.value
    }
    if (v.kind === 'date') {
        return v.value
    }
    if (v.kind === 'text') {
        const n = Number(v.value)
        return Number.isFinite(n) ? n : undefined
    }
    if (v.kind === 'bool') {
        return v.value ? 1 : 0
    }
    return undefined
}

function buildBuiltins(): Record<string, Fn> {
    const fns: Record<string, Fn> = {}

    // Array constructor: Array(a,b,c)
    fns[normalizeName('Array')] = (args: FormulaValue[]) => {
        const outText: string[] = []
        const outNumber: number[] = []
        let hasText = false
        let hasNumber = false
        for (const a of args) {
            if (a.kind === 'text') {
                outText.push(a.value)
                hasText = true
                continue
            }
            if (a.kind === 'number') {
                outNumber.push(a.value)
                hasNumber = true
                continue
            }
            // arrays in formulas must be string[] or number[]
            return ERROR
        }
        if (hasText && hasNumber) return ERROR
        if (hasNumber) return { kind: 'numberArray', value: outNumber }
        return { kind: 'stringArray', value: outText }
    }

    // Date constructor: Date(timestamp)
    fns[normalizeName('Date')] = (args: FormulaValue[]) => {
        if (args.length !== 1) return ERROR
        const ts = numberFromUnknown(args[0])
        if (ts === undefined || !Number.isFinite(ts)) return ERROR
        return { kind: 'date', value: ts }
    }

    // number functions
    fns[normalizeName('Max')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        const wantsDate = list.some(v => v.kind === 'date')
        if (list.length === 1 && (list[0].kind === 'stringArray' || list[0].kind === 'numberArray')) {
            const nums = list[0].kind === 'numberArray' ? asNumberArray(list[0]) || [] : (asArray(list[0]) || []).map(s => Number(s))
            if (nums.length === 0) return ERROR
            if (nums.some(n => !Number.isFinite(n))) return ERROR
            const v = Math.max(...nums)
            return wantsDate ? { kind: 'date', value: v } : { kind: 'number', value: v }
        }
        if (list.length === 0) return ERROR
        const nums = list.map(asNumber)
        if (nums.some(n => n === undefined)) return ERROR
        const v = Math.max(...(nums as number[]))
        return wantsDate ? { kind: 'date', value: v } : { kind: 'number', value: v }
    }
    fns[normalizeName('Min')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        const wantsDate = list.some(v => v.kind === 'date')
        if (list.length === 1 && (list[0].kind === 'stringArray' || list[0].kind === 'numberArray')) {
            const nums = list[0].kind === 'numberArray' ? asNumberArray(list[0]) || [] : (asArray(list[0]) || []).map(s => Number(s))
            if (nums.length === 0) return ERROR
            if (nums.some(n => !Number.isFinite(n))) return ERROR
            const v = Math.min(...nums)
            return wantsDate ? { kind: 'date', value: v } : { kind: 'number', value: v }
        }
        if (list.length === 0) return ERROR
        const nums = list.map(asNumber)
        if (nums.some(n => n === undefined)) return ERROR
        const v = Math.min(...(nums as number[]))
        return wantsDate ? { kind: 'date', value: v } : { kind: 'number', value: v }
    }
    fns[normalizeName('Sum')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length === 1 && (list[0].kind === 'stringArray' || list[0].kind === 'numberArray')) {
            const nums = list[0].kind === 'numberArray' ? asNumberArray(list[0]) || [] : (asArray(list[0]) || []).map(s => Number(s))
            if (nums.some(n => !Number.isFinite(n))) return ERROR
            return { kind: 'number', value: nums.reduce((p, c) => p + c, 0) }
        }
        const nums = list.map(asNumber)
        if (nums.some(n => n === undefined)) return ERROR
        return { kind: 'number', value: (nums as number[]).reduce((p, c) => p + c, 0) }
    }
    fns[normalizeName('Avg')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length === 1 && (list[0].kind === 'stringArray' || list[0].kind === 'numberArray')) {
            const nums = list[0].kind === 'numberArray' ? asNumberArray(list[0]) || [] : (asArray(list[0]) || []).map(s => Number(s))
            if (nums.length === 0) return ERROR
            if (nums.some(n => !Number.isFinite(n))) return ERROR
            const sum = nums.reduce((p, c) => p + c, 0)
            return { kind: 'number', value: sum / nums.length }
        }
        if (list.length === 0) return ERROR
        const nums = list.map(asNumber)
        if (nums.some(n => n === undefined)) return ERROR
        const sum = (nums as number[]).reduce((p, c) => p + c, 0)
        return { kind: 'number', value: sum / (nums as number[]).length }
    }
    fns[normalizeName('Round')] = args => {
        if (!requireArgs(args, 1)) return ERROR
        const n = asNumber(args[0])
        if (n === undefined) return ERROR
        const precision = args.length > 1 ? asNumber(args[1]) : 0
        if (precision === undefined) return ERROR
        const p = Math.trunc(precision)
        if (p === 0) {
            return { kind: 'number', value: Math.round(n) }
        }
        if (p > 0) {
            const factor = Math.pow(10, p)
            return { kind: 'number', value: Math.round(n * factor) / factor }
        }
        const factor = Math.pow(10, -p)
        return { kind: 'number', value: Math.round(n / factor) * factor }
    }
    fns[normalizeName('RoundUp')] = args => {
        if (!requireArgs(args, 1)) return ERROR
        const n = asNumber(args[0])
        if (n === undefined) return ERROR
        const precision = args.length > 1 ? asNumber(args[1]) : 0
        if (precision === undefined) return ERROR
        const p = Math.trunc(precision)
        const sign = n >= 0 ? 1 : -1
        const abs = Math.abs(n)
        if (p === 0) return { kind: 'number', value: sign * Math.ceil(abs) }
        if (p > 0) {
            const factor = Math.pow(10, p)
            return { kind: 'number', value: sign * (Math.ceil(abs * factor) / factor) }
        }
        const factor = Math.pow(10, -p)
        return { kind: 'number', value: sign * (Math.ceil(abs / factor) * factor) }
    }
    fns[normalizeName('RoundDown')] = args => {
        if (!requireArgs(args, 1)) return ERROR
        const n = asNumber(args[0])
        if (n === undefined) return ERROR
        const precision = args.length > 1 ? asNumber(args[1]) : 0
        if (precision === undefined) return ERROR
        const p = Math.trunc(precision)
        const sign = n >= 0 ? 1 : -1
        const abs = Math.abs(n)
        if (p === 0) return { kind: 'number', value: sign * Math.floor(abs) }
        if (p > 0) {
            const factor = Math.pow(10, p)
            return { kind: 'number', value: sign * (Math.floor(abs * factor) / factor) }
        }
        const factor = Math.pow(10, -p)
        return { kind: 'number', value: sign * (Math.floor(abs / factor) * factor) }
    }
    fns[normalizeName('Mod')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 2) return ERROR
        const a = asNumber(list[0])
        const b = asNumber(list[1])
        if (a === undefined || b === undefined || b === 0) return ERROR
        // spec: sign follows divisor
        const result = ((a % b) + b) % b
        return { kind: 'number', value: result }
    }
    fns[normalizeName('Abs')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 1) return ERROR
        const n = asNumber(list[0])
        if (n === undefined) return ERROR
        return { kind: 'number', value: Math.abs(n) }
    }
    fns[normalizeName('ToText')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length < 1) return ERROR
        if (list[0].kind === 'date' && list.length >= 2) {
            const formatStr = asText(list[1])
            if (formatStr === undefined) return ERROR
            try {
                return { kind: 'text', value: format(new Date(list[0].value), formatStr) }
            } catch {
                return ERROR
            }
        }
        if (list[0].kind === 'stringArray') {
            return { kind: 'text', value: list[0].value.join(',') }
        }
        const n = asNumber(list[0])
        if (n === undefined) return ERROR
        return { kind: 'text', value: String(n) }
    }

    // text functions
    fns[normalizeName('Concat')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        const parts = list.map(v => {
            if (v.kind === 'text') return v.value
            if (v.kind === 'number') return String(v.value)
            if (v.kind === 'bool') return v.value ? 'true' : 'false'
            if (v.kind === 'null') return ''
            return ''
        })
        return { kind: 'text', value: parts.join('') }
    }
    fns[normalizeName('StartsWith')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 2) return ERROR
        const text = asText(list[0])
        const prefix = asText(list[1])
        if (text === undefined || prefix === undefined) return ERROR
        return { kind: 'bool', value: text.startsWith(prefix) }
    }
    fns[normalizeName('EndsWith')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 2) return ERROR
        const text = asText(list[0])
        const suffix = asText(list[1])
        if (text === undefined || suffix === undefined) return ERROR
        return { kind: 'bool', value: text.endsWith(suffix) }
    }
    fns[normalizeName('Slice')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length < 2) return ERROR
        const text = asText(list[0])
        const start = asNumber(list[1])
        if (text === undefined || start === undefined) return ERROR
        const from = Math.max(1, Math.trunc(start)) - 1
        if (list.length < 3) {
            return { kind: 'text', value: text.slice(from) }
        }
        const length = asNumber(list[2])
        if (length === undefined) return ERROR
        const count = Math.trunc(length)
        if (count <= 0) {
            return { kind: 'text', value: '' }
        }
        return { kind: 'text', value: text.slice(from, from + count) }
    }
    fns[normalizeName('ToNumber')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 1) return ERROR
        const text = asText(list[0])
        if (text === undefined) return ERROR
        const m = text.match(/-?\d+(\.\d+)?/)
        if (!m) return ERROR
        const n = Number(m[0])
        if (!Number.isFinite(n)) return ERROR
        return { kind: 'number', value: n }
    }
    fns[normalizeName('ToArray')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 1) return ERROR
        const text = asText(list[0])
        if (text === undefined) return ERROR
        const parts = text.split(',')
        return { kind: 'stringArray', value: parts }
    }
    fns[normalizeName('ToDate')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length < 1 || list.length > 2) return ERROR
        const text = asText(list[0])
        if (text === undefined) return ERROR
        const format = list.length > 1 ? asText(list[1]) : undefined
        if (list[1] && format === undefined) return ERROR

        // No format: fall back to native parse
        if (!format) {
            const t = Date.parse(text)
            return Number.isFinite(t) ? { kind: 'date', value: t } : ERROR
        }

        // Normalize tokens: YYYY/MM/DD -> yyyy/MM/dd
        const fmt = format.replaceAll('YYYY', 'yyyy').replaceAll('YY', 'yy').replaceAll('DD', 'dd').replaceAll('D', 'd')

        const d = parse(text, fmt, new Date())
        if (!isValid(d)) return ERROR
        return { kind: 'date', value: d.getTime() }
    }
    fns[normalizeName('Len')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 1) return ERROR
        const text = asText(list[0])
        if (text === undefined) return ERROR
        return { kind: 'number', value: text.length }
    }
    fns[normalizeName('Contains')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        // Overload: text.Contains(subText) OR array.Contains(value)
        if (list.length !== 2) return ERROR
        const text = asText(list[0])
        if (text !== undefined) {
            const sub = asText(list[1])
            if (sub === undefined) return ERROR
            return { kind: 'bool', value: text.includes(sub) }
        }
        const arr = asArray(list[0])
        if (arr) {
            const v = asText(list[1])
            if (v === undefined) return ERROR
            return { kind: 'bool', value: arr.includes(v) }
        }
        return ERROR
    }

    // logic functions
    fns[normalizeName('If')] = args => {
        if (args.length !== 3) return ERROR
        const cond = boolFromUnknown(args[0])
        if (cond === undefined) return ERROR
        return cond ? args[1] : args[2]
    }
    fns[normalizeName('And')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length === 0) return ERROR
        for (const v of list) {
            const b = boolFromUnknown(v)
            if (b === undefined) return ERROR
            if (!b) return { kind: 'bool', value: false }
        }
        return { kind: 'bool', value: true }
    }
    fns[normalizeName('Or')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length === 0) return ERROR
        for (const v of list) {
            const b = boolFromUnknown(v)
            if (b === undefined) return ERROR
            if (b) return { kind: 'bool', value: true }
        }
        return { kind: 'bool', value: false }
    }
    fns[normalizeName('Not')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 1) return ERROR
        const b = boolFromUnknown(list[0])
        if (b === undefined) return ERROR
        return { kind: 'bool', value: !b }
    }
    fns[normalizeName('Switch')] = args => {
        if (args.length < 3) return ERROR
        const expr = args[0]
        // pairs: pattern,result ... optional default
        const pairs = args.slice(1)
        const hasDefault = pairs.length % 2 === 1
        const limit = hasDefault ? pairs.length - 1 : pairs.length
        for (let i = 0; i < limit; i += 2) {
            if (equalsValue(expr, pairs[i])) {
                return pairs[i + 1]
            }
        }
        return hasDefault ? pairs[pairs.length - 1] : NULL
    }
    fns[normalizeName('SwitchIf')] = args => {
        if (args.length < 2) return ERROR
        const hasDefault = args.length % 2 === 1
        const limit = hasDefault ? args.length - 1 : args.length
        for (let i = 0; i < limit; i += 2) {
            const cond = boolFromUnknown(args[i])
            if (cond === undefined) return ERROR
            if (cond) return args[i + 1]
        }
        return hasDefault ? args[args.length - 1] : NULL
    }

    // array functions
    fns[normalizeName('ContainsAll')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length < 2) return ERROR
        if (list[0].kind !== 'stringArray') return ERROR
        const arr = asArray(list[0])
        if (!arr) return ERROR
        const targets = list.slice(1)
        const texts: string[] = []
        for (const t of targets) {
            const tt = asText(t)
            if (tt === undefined) return ERROR
            texts.push(tt)
        }
        return { kind: 'bool', value: texts.every(t => arr.includes(t)) }
    }
    fns[normalizeName('Count')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 1) return ERROR
        const len = asAnyArrayLen(list[0])
        if (len === undefined) return ERROR
        return { kind: 'number', value: len }
    }
    fns[normalizeName('First')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 1) return ERROR
        if (list[0].kind === 'stringArray') {
            const arr = asArray(list[0]) || []
            return arr.length > 0 ? { kind: 'text', value: arr[0] } : NULL
        }
        if (list[0].kind === 'numberArray') {
            const arr = asNumberArray(list[0]) || []
            return arr.length > 0 ? { kind: 'number', value: arr[0] } : NULL
        }
        return ERROR
    }

    fns[normalizeName('Append')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 2) return ERROR
        const arrayValue = list[0]
        const newValue = list[1]
        if (arrayValue.kind === 'stringArray') {
            const text = asText(newValue)
            if (text === undefined) return ERROR
            return { kind: 'stringArray', value: [...arrayValue.value, text] }
        }
        if (arrayValue.kind === 'numberArray') {
            const num = asNumber(newValue)
            if (num === undefined) return ERROR
            return { kind: 'numberArray', value: [...arrayValue.value, num] }
        }
        return ERROR
    }

    fns[normalizeName('Remove')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 2) return ERROR
        const arrayValue = list[0]
        const removeValue = list[1]
        if (arrayValue.kind === 'stringArray') {
            const text = asText(removeValue)
            if (text === undefined) return ERROR
            const idx = arrayValue.value.indexOf(text)
            if (idx < 0) return { kind: 'stringArray', value: [...arrayValue.value] }
            return {
                kind: 'stringArray',
                value: [...arrayValue.value.slice(0, idx), ...arrayValue.value.slice(idx + 1)]
            }
        }
        if (arrayValue.kind === 'numberArray') {
            const num = asNumber(removeValue)
            if (num === undefined) return ERROR
            const idx = arrayValue.value.indexOf(num)
            if (idx < 0) return { kind: 'numberArray', value: [...arrayValue.value] }
            return {
                kind: 'numberArray',
                value: [...arrayValue.value.slice(0, idx), ...arrayValue.value.slice(idx + 1)]
            }
        }
        return ERROR
    }

    fns[normalizeName('Update')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 3) return ERROR
        const arrayValue = list[0]
        const indexValue = asNumber(list[1])
        if (indexValue === undefined) return ERROR
        const index = Math.trunc(indexValue)
        if (index < 0) return ERROR
        if (arrayValue.kind === 'stringArray') {
            const text = asText(list[2])
            if (text === undefined) return ERROR
            if (index >= arrayValue.value.length) return ERROR
            const next = [...arrayValue.value]
            next[index] = text
            return { kind: 'stringArray', value: next }
        }
        if (arrayValue.kind === 'numberArray') {
            const num = asNumber(list[2])
            if (num === undefined) return ERROR
            if (index >= arrayValue.value.length) return ERROR
            const next = [...arrayValue.value]
            next[index] = num
            return { kind: 'numberArray', value: next }
        }
        return ERROR
    }

    fns[normalizeName('FindIndex')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 2) return ERROR
        const arrayValue = list[0]
        const conditionText = asText(list[1])
        if (!conditionText) return ERROR
        if (arrayValue.kind === 'stringArray') {
            for (let i = 0; i < arrayValue.value.length; i++) {
                const currentValue: FormulaValue = { kind: 'text', value: arrayValue.value[i] }
                const expression = replaceCurrentValuePlaceholder(conditionText, currentValue)
                const result = evaluateConditionExpression(expression)
                const ok = asBool(result)
                if (ok === undefined) return ERROR
                if (ok) return { kind: 'number', value: i }
            }
            return { kind: 'number', value: -1 }
        }
        if (arrayValue.kind === 'numberArray') {
            for (let i = 0; i < arrayValue.value.length; i++) {
                const currentValue: FormulaValue = { kind: 'number', value: arrayValue.value[i] }
                const expression = replaceCurrentValuePlaceholder(conditionText, currentValue)
                const result = evaluateConditionExpression(expression)
                const ok = asBool(result)
                if (ok === undefined) return ERROR
                if (ok) return { kind: 'number', value: i }
            }
            return { kind: 'number', value: -1 }
        }
        return ERROR
    }

    fns[normalizeName('Filter')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 2) return ERROR
        const arrayValue = list[0]
        const conditionText = asText(list[1])
        if (!conditionText) return ERROR
        if (arrayValue.kind === 'stringArray') {
            const out: string[] = []
            for (let i = 0; i < arrayValue.value.length; i++) {
                const currentValue: FormulaValue = { kind: 'text', value: arrayValue.value[i] }
                const expression = replaceCurrentValuePlaceholder(conditionText, currentValue)
                const result = evaluateConditionExpression(expression)
                const ok = asBool(result)
                if (ok === undefined) return ERROR
                if (ok) out.push(arrayValue.value[i])
            }
            return { kind: 'stringArray', value: out }
        }
        if (arrayValue.kind === 'numberArray') {
            const out: number[] = []
            for (let i = 0; i < arrayValue.value.length; i++) {
                const currentValue: FormulaValue = { kind: 'number', value: arrayValue.value[i] }
                const expression = replaceCurrentValuePlaceholder(conditionText, currentValue)
                const result = evaluateConditionExpression(expression)
                const ok = asBool(result)
                if (ok === undefined) return ERROR
                if (ok) out.push(arrayValue.value[i])
            }
            return { kind: 'numberArray', value: out }
        }
        return ERROR
    }

    // other
    fns[normalizeName('IsBlank')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 1) return ERROR
        const v = list[0]
        if (v.kind === 'null') return { kind: 'bool', value: true }
        if (v.kind === 'text') return { kind: 'bool', value: !v.value || v.value.trim().length === 0 }
        if (v.kind === 'stringArray') return { kind: 'bool', value: !v.value || v.value.length === 0 }
        if (v.kind === 'numberArray') return { kind: 'bool', value: !v.value || v.value.length === 0 }
        if (v.kind === 'error') return { kind: 'bool', value: true }
        return { kind: 'bool', value: false }
    }

    // date functions (date is timestamp number, but preserve DATE kind)
    fns[normalizeName('Now')] = () => ({ kind: 'date', value: Date.now() })
    fns[normalizeName('Year')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 1) return ERROR
        const ts = asNumber(list[0])
        if (ts === undefined) return ERROR
        return { kind: 'number', value: getYear(ts) }
    }
    fns[normalizeName('Month')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 1) return ERROR
        const ts = asNumber(list[0])
        if (ts === undefined) return ERROR
        return { kind: 'number', value: getMonth(ts) + 1 }
    }
    fns[normalizeName('Day')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 1) return ERROR
        const ts = asNumber(list[0])
        if (ts === undefined) return ERROR
        return { kind: 'number', value: getDate(ts) }
    }
    fns[normalizeName('Hour')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 1) return ERROR
        const ts = asNumber(list[0])
        if (ts === undefined) return ERROR
        return { kind: 'number', value: getHours(ts) }
    }
    fns[normalizeName('Minute')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 1) return ERROR
        const ts = asNumber(list[0])
        if (ts === undefined) return ERROR
        return { kind: 'number', value: getMinutes(ts) }
    }
    fns[normalizeName('Second')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 1) return ERROR
        const ts = asNumber(list[0])
        if (ts === undefined) return ERROR
        return { kind: 'number', value: getSeconds(ts) }
    }
    fns[normalizeName('WeekDay')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length < 1 || list.length > 2) return ERROR
        const ts = asNumber(list[0])
        if (ts === undefined) return ERROR
        const type = list.length > 1 ? asNumber(list[1]) : 1
        if (type === undefined) return ERROR
        const d = getDay(ts) // 0..6, 0 is Sunday
        if (type === 1) {
            // Sunday=1 .. Saturday=7
            return { kind: 'number', value: d + 1 }
        }
        // type=2: Monday=1 .. Sunday=7
        return { kind: 'number', value: d === 0 ? 7 : d }
    }
    fns[normalizeName('WeekNum')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length < 1 || list.length > 2) return ERROR
        const ts = asNumber(list[0])
        if (ts === undefined) return ERROR
        const type = list.length > 1 ? asNumber(list[1]) : 1
        if (type === undefined) return ERROR
        // type: 1 => week starts Sunday; 2 => Monday
        const weekStartsOn = type === 2 ? 1 : 0
        return { kind: 'number', value: getWeek(ts, { weekStartsOn }) }
    }
    fns[normalizeName('DateDif')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 3) return ERROR
        const start = asNumber(list[0])
        const end = asNumber(list[1])
        const unit = asText(list[2])
        if (start === undefined || end === undefined || unit === undefined) return ERROR
        switch (unit) {
            case 'y':
                return { kind: 'number', value: differenceInYears(end, start) }
            case 'M':
                return { kind: 'number', value: differenceInMonths(end, start) }
            case 'w':
                return { kind: 'number', value: differenceInWeeks(end, start) }
            case 'd':
                return { kind: 'number', value: differenceInDays(end, start) }
            case 'h':
                return { kind: 'number', value: differenceInHours(end, start) }
            case 'm':
                return { kind: 'number', value: differenceInMinutes(end, start) }
            case 's':
                return { kind: 'number', value: differenceInSeconds(end, start) }
            default:
                return ERROR
        }
    }
    fns[normalizeName('DateAdd')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 3) return ERROR
        const ts = asNumber(list[0])
        const count = asNumber(list[1])
        const unit = asText(list[2])
        if (ts === undefined || count === undefined || unit === undefined) return ERROR
        const c = Math.trunc(count)
        switch (unit) {
            case 's':
                return { kind: 'date', value: addSeconds(ts, c).getTime() }
            case 'm':
                return { kind: 'date', value: addMinutes(ts, c).getTime() }
            case 'h':
                return { kind: 'date', value: addHours(ts, c).getTime() }
            case 'd':
                return { kind: 'date', value: addDays(ts, c).getTime() }
            case 'w':
                return { kind: 'date', value: addWeeks(ts, c).getTime() }
            case 'M':
                return { kind: 'date', value: addMonths(ts, c).getTime() }
            case 'y':
                return { kind: 'date', value: addYears(ts, c).getTime() }
            default:
                return ERROR
        }
    }

    fns[normalizeName('IsSame')] = (args, receiver) => {
        const list = receiver ? [receiver, ...args] : args
        if (list.length !== 3) return ERROR
        const t1 = asNumber(list[0])
        const t2 = asNumber(list[1])
        const unit = asText(list[2])
        if (t1 === undefined || t2 === undefined || unit === undefined) return ERROR
        const d1 = new Date(t1)
        const d2 = new Date(t2)
        switch (unit) {
            case 's':
                return { kind: 'bool', value: isSameSecond(d1, d2) }
            case 'm':
                return { kind: 'bool', value: isSameMinute(d1, d2) }
            case 'h':
                return { kind: 'bool', value: isSameHour(d1, d2) }
            case 'd':
                return { kind: 'bool', value: isSameDay(d1, d2) }
            case 'M':
                return { kind: 'bool', value: isSameMonth(d1, d2) }
            case 'y':
                return { kind: 'bool', value: isSameYear(d1, d2) }
            default:
                return ERROR
        }
    }

    return fns
}

const BUILTINS = buildBuiltins()

function evalAst(node: any): FormulaValue {
    if (!node || typeof node !== 'object') {
        return ERROR
    }

    switch (node.type) {
        case 'Literal': {
            const v = node.value
            if (v === null || v === undefined) return NULL
            if (typeof v === 'number') return Number.isFinite(v) ? { kind: 'number', value: v } : NULL
            if (typeof v === 'boolean') return { kind: 'bool', value: v }
            if (typeof v === 'string') return { kind: 'text', value: v }
            return ERROR
        }
        case 'UnaryExpression': {
            const arg = evalAst(node.argument)
            if (isError(arg)) return ERROR
            const op = node.operator
            if (op === '!') {
                const b = boolFromUnknown(arg)
                return b === undefined ? ERROR : { kind: 'bool', value: !b }
            }
            if (op === '+') {
                const n = numberFromUnknown(arg)
                return n === undefined ? ERROR : { kind: 'number', value: n }
            }
            if (op === '-') {
                const n = numberFromUnknown(arg)
                return n === undefined ? ERROR : { kind: 'number', value: -n }
            }
            return ERROR
        }
        case 'BinaryExpression': {
            const left = evalAst(node.left)
            const right = evalAst(node.right)
            if (isError(left) || isError(right)) return ERROR
            const op = node.operator

            // arithmetic
            if (op === '+' || op === '-' || op === '*' || op === '/' || op === '%') {
                const ln = numberFromUnknown(left)
                const rn = numberFromUnknown(right)
                if (ln === undefined || rn === undefined) return ERROR
                if (op === '+') return { kind: 'number', value: ln + rn }
                if (op === '-') return { kind: 'number', value: ln - rn }
                if (op === '*') return { kind: 'number', value: ln * rn }
                if (op === '/') return { kind: 'number', value: ln / rn }
                return { kind: 'number', value: ln % rn }
            }

            // comparisons
            if (op === '>' || op === '>=' || op === '<' || op === '<=') {
                const ln = numberFromUnknown(left)
                const rn = numberFromUnknown(right)
                if (ln === undefined || rn === undefined) return ERROR
                if (op === '>') return { kind: 'bool', value: ln > rn }
                if (op === '>=') return { kind: 'bool', value: ln >= rn }
                if (op === '<') return { kind: 'bool', value: ln < rn }
                return { kind: 'bool', value: ln <= rn }
            }
            if (op === '==') {
                return { kind: 'bool', value: equalsValue(left, right) }
            }
            if (op === '!=') {
                return { kind: 'bool', value: !equalsValue(left, right) }
            }
            if (op === '===' || op === '!==') {
                return ERROR
            }

            // logical (treat & | as And/Or to match docs)
            if (op === '&&' || op === '&') {
                const lb = boolFromUnknown(left)
                const rb = boolFromUnknown(right)
                if (lb === undefined || rb === undefined) return ERROR
                return { kind: 'bool', value: lb && rb }
            }
            if (op === '||' || op === '|') {
                const lb = boolFromUnknown(left)
                const rb = boolFromUnknown(right)
                if (lb === undefined || rb === undefined) return ERROR
                return { kind: 'bool', value: lb || rb }
            }
            return ERROR
        }
        case 'Identifier': {
            // Identifiers should only appear as callees; free identifiers are not supported
            return ERROR
        }
        case 'CallExpression': {
            // Short-circuit evaluation for If(condition, value1, value2):
            // only evaluate the selected branch to avoid error propagation from the unselected one.
            if (node.callee?.type === 'Identifier') {
                const calleeName = normalizeName(node.callee.name || '')
                if (calleeName === 'if') {
                    const rawArgs = node.arguments || []
                    if (rawArgs.length !== 3) return ERROR
                    const condValue = evalAst(rawArgs[0])
                    if (isError(condValue)) return ERROR
                    const cond = boolFromUnknown(condValue)
                    if (cond === undefined) return ERROR
                    const branchValue = evalAst(rawArgs[cond ? 1 : 2])
                    return branchValue
                }
            }

            const args = (node.arguments || []).map((a: any) => evalAst(a))
            if (args.some(isError)) return ERROR

            // foo(...)
            if (node.callee?.type === 'Identifier') {
                const name = normalizeName(node.callee.name || '')
                const fn = BUILTINS[name]
                if (!fn) return ERROR
                const res = fn(args)
                return res
            }

            // obj.method(...)
            if (node.callee?.type === 'MemberExpression') {
                const receiver = evalAst(node.callee.object)
                if (isError(receiver)) return ERROR
                const prop = node.callee.property
                const methodName = prop?.name || prop?.value
                if (!methodName || typeof methodName !== 'string') return ERROR
                const name = normalizeName(methodName)
                const fn = BUILTINS[name]
                if (!fn) return ERROR
                return fn(args, receiver)
            }

            return ERROR
        }
        default:
            return ERROR
    }
}

function serializeArray(values: unknown[]): string {
    const items = values.map(v => serializeUnknown(v))
    return `Array(${items.join(',')})`
}

function serializeCurrentValueForExpression(v: FormulaValue): string {
    if (v.kind === 'number' || v.kind === 'date') {
        return String(v.value)
    }
    if (v.kind === 'bool') {
        return v.value ? 'true' : 'false'
    }
    if (v.kind === 'text') {
        return JSON.stringify(v.value)
    }
    if (v.kind === 'null') {
        return 'null'
    }
    return 'null'
}

function replaceCurrentValuePlaceholder(expression: string, v: FormulaValue): string {
    const replacement = serializeCurrentValueForExpression(v)
    return expression.replace(/\{\s*currentValue\s*\}/gi, replacement)
}

function evaluateConditionExpression(expression: string): FormulaValue {
    if (!expression.trim()) {
        return ERROR
    }
    if (hasUnquotedCurly(expression)) {
        return ERROR
    }
    const ast = jsep(rewriteNumericMemberAccess(rewriteArrayLiterals(expression)))
    return evalAst(ast)
}

function hasUnquotedCurly(input: string): boolean {
    let inSingle = false
    let inDouble = false
    let escape = false
    for (let i = 0; i < input.length; i++) {
        const ch = input[i]
        if (escape) {
            escape = false
            continue
        }
        if (ch === '\\') {
            escape = true
            continue
        }
        if (!inDouble && ch === "'") {
            inSingle = !inSingle
            continue
        }
        if (!inSingle && ch === '"') {
            inDouble = !inDouble
            continue
        }
        if (!inSingle && !inDouble && (ch === '{' || ch === '}')) {
            return true
        }
    }
    return false
}

function hasBalancedUnquotedCurly(input: string): boolean {
    let depth = 0
    let inSingle = false
    let inDouble = false
    let escape = false
    for (let i = 0; i < input.length; i++) {
        const ch = input[i]
        if (escape) {
            escape = false
            continue
        }
        if (ch === '\\') {
            escape = true
            continue
        }
        if (!inDouble && ch === "'") {
            inSingle = !inSingle
            continue
        }
        if (!inSingle && ch === '"') {
            inDouble = !inDouble
            continue
        }
        if (!inSingle && !inDouble) {
            if (ch === '{') depth++
            if (ch === '}') depth--
            if (depth < 0) return false
        }
    }
    return depth === 0
}

function splitTopLevelArgs(input: string): string[] {
    const args: string[] = []
    let start = 0
    let depthParen = 0
    let depthBracket = 0
    let inSingle = false
    let inDouble = false
    let escape = false
    for (let i = 0; i < input.length; i++) {
        const ch = input[i]
        if (escape) {
            escape = false
            continue
        }
        if (ch === '\\') {
            escape = true
            continue
        }
        if (!inDouble && ch === "'") {
            inSingle = !inSingle
            continue
        }
        if (!inSingle && ch === '"') {
            inDouble = !inDouble
            continue
        }
        if (inSingle || inDouble) continue
        if (ch === '(') depthParen++
        if (ch === ')') depthParen--
        if (ch === '[') depthBracket++
        if (ch === ']') depthBracket--
        if (ch === ',' && depthParen === 0 && depthBracket === 0) {
            args.push(input.slice(start, i).trim())
            start = i + 1
        }
    }
    const tail = input.slice(start).trim()
    if (tail.length > 0 || input.trim().length > 0) {
        args.push(tail)
    }
    return args
}

function findMatchingParen(input: string, startIndex: number): number | undefined {
    let depth = 0
    let inSingle = false
    let inDouble = false
    let escape = false
    for (let i = startIndex; i < input.length; i++) {
        const ch = input[i]
        if (escape) {
            escape = false
            continue
        }
        if (ch === '\\') {
            escape = true
            continue
        }
        if (!inDouble && ch === "'") {
            inSingle = !inSingle
            continue
        }
        if (!inSingle && ch === '"') {
            inDouble = !inDouble
            continue
        }
        if (inSingle || inDouble) continue
        if (ch === '(') depth++
        if (ch === ')') {
            depth--
            if (depth === 0) return i
        }
    }
    return undefined
}

function isWordChar(ch: string): boolean {
    return /[A-Za-z0-9_]/.test(ch)
}

function matchCallName(input: string, index: number): string | undefined {
    const names = ['FindIndex', 'Filter']
    for (const name of names) {
        if (input.slice(index, index + name.length).toLowerCase() === name.toLowerCase()) {
            const before = index > 0 ? input[index - 1] : ''
            const after = index + name.length < input.length ? input[index + name.length] : ''
            if ((before && isWordChar(before)) || (after && isWordChar(after))) {
                continue
            }
            return name
        }
    }
    return undefined
}

function rewriteConditionPlaceholders(input: string): string | undefined {
    const n = input.length
    let i = 0
    let out = ''
    let inSingle = false
    let inDouble = false
    let escape = false

    const skipSpaces = (idx: number) => {
        let j = idx
        while (j < n && /\s/.test(input[j])) j++
        return j
    }

    while (i < n) {
        const ch = input[i]
        if (escape) {
            out += ch
            escape = false
            i++
            continue
        }
        if (ch === '\\') {
            out += ch
            escape = true
            i++
            continue
        }
        if (!inDouble && ch === "'") {
            inSingle = !inSingle
            out += ch
            i++
            continue
        }
        if (!inSingle && ch === '"') {
            inDouble = !inDouble
            out += ch
            i++
            continue
        }

        if (!inSingle && !inDouble) {
            const name = matchCallName(input, i)
            if (name) {
                const afterName = skipSpaces(i + name.length)
                if (input[afterName] === '(') {
                    const endIndex = findMatchingParen(input, afterName)
                    if (endIndex === undefined) {
                        return undefined
                    }
                    const argsContent = input.slice(afterName + 1, endIndex)
                    const args = splitTopLevelArgs(argsContent)
                    let checkIndex = 1
                    let back = i - 1
                    while (back >= 0 && /\s/.test(input[back])) back--
                    if (back >= 0 && input[back] === '.') {
                        checkIndex = 0
                    }
                    if (args.length > checkIndex && hasUnquotedCurly(args[checkIndex])) {
                        if (!hasBalancedUnquotedCurly(args[checkIndex])) {
                            return undefined
                        }
                        args[checkIndex] = JSON.stringify(args[checkIndex])
                    }
                    const rewritten = `${input.slice(i, afterName)}(${args.join(',')})`
                    out += rewritten
                    i = endIndex + 1
                    continue
                }
            }
        }

        out += ch
        i++
    }

    if (hasUnquotedCurly(out)) {
        return undefined
    }
    return out
}

function isDigit(ch: string): boolean {
    return ch >= '0' && ch <= '9'
}

function isLetterOrUnderscore(ch: string): boolean {
    return (ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z') || ch === '_'
}

/**
 * Rewrites numeric member access so jsep can parse it: 6.IsBlank() -> (6).IsBlank().
 * In JS, 6. is parsed as number 6.0, so 6.IsBlank is invalid; wrapping in parens fixes it.
 */
function rewriteNumericMemberAccess(input: string): string {
    const n = input.length
    let i = 0
    let out = ''
    let inSingle = false
    let inDouble = false
    let escape = false

    while (i < n) {
        const ch = input[i]

        if (escape) {
            out += ch
            escape = false
            i++
            continue
        }
        if (ch === '\\') {
            out += ch
            escape = true
            i++
            continue
        }
        if (!inDouble && ch === "'") {
            inSingle = !inSingle
            out += ch
            i++
            continue
        }
        if (!inSingle && ch === '"') {
            inDouble = !inDouble
            out += ch
            i++
            continue
        }

        if (!inSingle && !inDouble && isDigit(ch)) {
            const start = i
            while (i < n && isDigit(input[i])) i++
            if (i < n && input[i] === '.') {
                if (i + 1 < n && isLetterOrUnderscore(input[i + 1])) {
                    out += `(${input.slice(start, i)}).`
                    i++
                    continue
                }
                if (i + 1 < n && isDigit(input[i + 1])) {
                    i++
                    while (i < n && isDigit(input[i])) i++
                    if (i < n && input[i] === '.' && i + 1 < n && isLetterOrUnderscore(input[i + 1])) {
                        out += `(${input.slice(start, i)}).`
                        i++
                        continue
                    }
                }
            }
            out += input.slice(start, i)
            continue
        }

        out += ch
        i++
    }
    return out
}

function rewriteArrayLiterals(input: string): string {
    const n = input.length
    let i = 0
    let out = ''
    let inSingle = false
    let inDouble = false
    let escape = false

    const flush = (s: string) => {
        out += s
    }

    while (i < n) {
        const ch = input[i]

        if (escape) {
            flush(ch)
            escape = false
            i++
            continue
        }

        if (ch === '\\') {
            flush(ch)
            escape = true
            i++
            continue
        }

        if (!inDouble && ch === "'") {
            inSingle = !inSingle
            flush(ch)
            i++
            continue
        }
        if (!inSingle && ch === '"') {
            inDouble = !inDouble
            flush(ch)
            i++
            continue
        }

        if (!inSingle && !inDouble && ch === '[') {
            // find matching ]
            let depth = 1
            let j = i + 1
            let inner = ''
            let innerSingle = false
            let innerDouble = false
            let innerEscape = false
            while (j < n && depth > 0) {
                const c = input[j]
                if (innerEscape) {
                    inner += c
                    innerEscape = false
                    j++
                    continue
                }
                if (c === '\\') {
                    inner += c
                    innerEscape = true
                    j++
                    continue
                }
                if (!innerDouble && c === "'") {
                    innerSingle = !innerSingle
                    inner += c
                    j++
                    continue
                }
                if (!innerSingle && c === '"') {
                    innerDouble = !innerDouble
                    inner += c
                    j++
                    continue
                }
                if (!innerSingle && !innerDouble) {
                    if (c === '[') {
                        depth++
                    } else if (c === ']') {
                        depth--
                        if (depth === 0) {
                            j++
                            break
                        }
                    }
                }
                inner += c
                j++
            }
            if (depth !== 0) {
                // unmatched bracket, keep as-is and let parser fail later
                flush(ch)
                i++
                continue
            }

            const rewrittenInner = rewriteArrayLiterals(inner.trim())
            flush(`Array(${rewrittenInner})`)
            i = j
            continue
        }

        flush(ch)
        i++
    }

    return out
}

function serializeUnknown(value: unknown): string {
    if (value === null || value === undefined) {
        return 'null'
    }
    if (typeof value === 'boolean') {
        return value ? 'true' : 'false'
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? String(value) : 'null'
    }
    if (typeof value === 'string') {
        return JSON.stringify(value)
    }
    if (Array.isArray(value)) {
        return serializeArray(value)
    }
    return 'null'
}

function serializeFieldValue(field: FieldADTValue): string {
    const innerType = field.innerType as TypeInstanceMap | undefined
    const v = (field as any).value as unknown

    switch (innerType) {
        case 'TEXT':
            return JSON.stringify(Array.isArray(v) ? v.join(',') : String(v ?? ''))
        case 'NUMBER': {
            if (Array.isArray(v)) {
                return serializeArray(v)
            }
            const n = typeof v === 'number' ? v : Number(v)
            return Number.isFinite(n) ? String(n) : 'null'
        }
        case 'DATE': {
            if (Array.isArray(v)) {
                // Date array is not supported in formula variables; serialize each item as Date(ts)
                const items = v.map(item => `Date(${serializeUnknown(item)})`)
                return `Array(${items.join(',')})`
            }
            const n = typeof v === 'number' ? v : Number(v)
            return Number.isFinite(n) ? `Date(${String(n)})` : 'null'
        }
        case 'BOOL': {
            if (typeof v === 'boolean') {
                return v ? 'true' : 'false'
            }
            return 'null'
        }
        case 'ARRAY': {
            if (Array.isArray(v)) {
                return serializeArray(v)
            }
            // some fields may store scalar but are declared ARRAY
            return serializeArray(v === undefined || v === null ? [] : [v])
        }
        case 'RICH_TEXT':
        case 'NULL':
        case 'RUNTIME':
        default:
            return serializeUnknown(v)
    }
}

export interface EvaluateFormulaToStringPayload {
    doc: JSONContent
    resolveVariableToField: (v: VariableADTvalue) => FieldADTValue | undefined
    onErrorText?: string
}

function evaluateFormulaInternal(payload: EvaluateFormulaToStringPayload): FormulaValue {
    const { doc, resolveVariableToField } = payload

    // Empty doc: treat as empty
    if (!doc || isEmpty(doc)) {
        return NULL
    }

    try {
        const expressionForParse = generateText(doc, {
            variable: {
                renderLabel: node => {
                    const v = node.attrs?.value as VariableADTvalue | undefined
                    if (!v) {
                        return 'null'
                    }
                    // avoid recursive formula-in-formula explosion
                    if (v.type === VariableType.FORMULA) {
                        return 'null'
                    }
                    const field = resolveVariableToField(v)
                    if (!field) {
                        return 'null'
                    }
                    return serializeFieldValue(field)
                }
            },
            currentValueMark: true
        })

        // Newly inserted formula may have no content; treat as empty instead of error.
        if (!expressionForParse || !expressionForParse.trim()) {
            return NULL
        }

        const rewrittenCondition = rewriteConditionPlaceholders(expressionForParse)
        if (!rewrittenCondition) {
            return ERROR
        }
        const ast = jsep(rewriteNumericMemberAccess(rewriteArrayLiterals(rewrittenCondition)))
        return evalAst(ast)
    } catch {
        return ERROR
    }
}

export function evaluateFormulaToString(payload: EvaluateFormulaToStringPayload): string {
    const { onErrorText = '{公式错误}' } = payload
    const res = evaluateFormulaInternal(payload)
    if (isError(res)) {
        return onErrorText
    }
    if (isNull(res)) {
        return ''
    }
    return toStringValue(res)
}

export interface EvaluateFormulaValuePayload extends Omit<EvaluateFormulaToStringPayload, 'onErrorText'> {
    expectedInnerType: TypeInstanceMap
}

export function evaluateFormulaValue<T extends BaseCellValue = BaseCellValue>(payload: EvaluateFormulaValuePayload): T | undefined {
    const { expectedInnerType, ...rest } = payload

    // TEXT uses evaluateFormulaToString by design
    if (expectedInnerType === 'TEXT') {
        return evaluateFormulaToString(rest) as T
    }

    const res = evaluateFormulaInternal(rest)
    if (isError(res) || isNull(res)) {
        return undefined
    }

    switch (expectedInnerType) {
        case 'NUMBER':
        case 'DATE': {
            if (expectedInnerType === 'NUMBER') {
                return res.kind === 'number' ? (res.value as T) : undefined
            }
            return res.kind === 'date' ? (res.value as T) : undefined
        }
        case 'BOOL': {
            return res.kind === 'bool' ? (res.value as T) : undefined
        }
        case 'ARRAY': {
            if (res.kind !== 'stringArray' && res.kind !== 'numberArray') {
                return undefined
            }
            if (res.kind === 'stringArray') {
                return res.value as T
            }
            return res.value.map(String) as T
        }
        case 'NULL':
        case 'RICH_TEXT':
        case 'RUNTIME':
        default:
            return undefined
    }
}

export interface EvaluateFormulaResultPayload extends Omit<EvaluateFormulaToStringPayload, 'onErrorText'> {}

export function evaluateFormulaResult(
    payload: EvaluateFormulaResultPayload
): { innerType: TypeInstanceMap; value: BaseCellValue } | undefined {
    const res = evaluateFormulaInternal(payload)
    if (isError(res) || isNull(res)) {
        return undefined
    }
    switch (res.kind) {
        case 'text':
            return { innerType: 'TEXT', value: res.value }
        case 'bool':
            return { innerType: 'BOOL', value: res.value }
        case 'number':
            return { innerType: 'NUMBER', value: res.value }
        case 'date':
            return { innerType: 'DATE', value: res.value }
        case 'stringArray': {
            return { innerType: 'ARRAY', value: res.value }
        }
        case 'numberArray': {
            // external ARRAY value type is string[]; keep numberArray runtime internal
            return { innerType: 'ARRAY', value: res.value.map(String) }
        }
        default:
            return undefined
    }
}
